import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Player, Question, GameStatus } from '../types/game.types';
import type { LifelineDto } from '../services/lifelineService';

// Simple Category type for game settings (compatible with both services)
export interface GameCategory {
  id: string;
  name: string;
  description?: string;
  imageUrl?: string;
  questionCount?: number;
}

// Game Settings for creation
export interface GameSettings {
  categories: GameCategory[];
  lifelines: LifelineDto[];
  gameName?: string;
  isFreeTrial?: boolean;
}

// Team structure for gameplay
export interface Team {
  id: string;
  name: string;
  memberCount: number;
  score: number;
  color: string;
  usedLifelines: string[];
  lifelines?: LifelineDto[]; // Team-specific lifelines
}

// Question cell in the game board
export interface QuestionCell {
  categoryId: string;
  categoryName: string;
  points: number;
  side: 'left' | 'right';
  isAnswered: boolean;
  answeredBy?: string; // team id
}

interface GameState {
  // Game creation settings
  gameSettings: GameSettings | null;
  
  // Teams
  team1: Team | null;
  team2: Team | null;
  currentTurn: 'team1' | 'team2';
  
  // Game board
  questionCells: QuestionCell[];
  
  // Current question state
  roomCode: string | null;
  players: Player[];
  currentQuestion: Question | null;
  currentQuestionIndex: number;
  totalQuestions: number;
  timeRemaining: number;
  gameStatus: GameStatus;
  myAnswer: string | null;
  usedLifelines: string[];
  
  // Current cell being played
  currentCell: QuestionCell | null;
  
  // الحفرة: تُفعّل قبل الدخول للسؤال (من لوحة اللعب)
  nextQuestionIsPit: boolean;
  
  // عدم تكرار السؤال للاعب الدافع - أسئلة معروضة في الجلسة الحالية
  shownQuestionIds: string[];
  
  // Actions - Game Settings
  setGameSettings: (settings: GameSettings) => void;
  
  // Actions - Teams
  setTeam1: (team: Team) => void;
  setTeam2: (team: Team) => void;
  switchTurn: () => void;
  addScore: (teamId: string, points: number) => void;
  subtractScore: (teamId: string, points: number) => void;
  useTeamLifeline: (teamId: string, lifelineId: string) => void;
  
  // Actions - Game Board
  initializeGameBoard: () => void;
  setCurrentCell: (cell: QuestionCell | null) => void;
  markCellAnswered: (categoryId: string, points: number, side: 'left' | 'right', teamId?: string) => void;
  setNextQuestionIsPit: (value: boolean) => void;
  addShownQuestionId: (questionId: string) => void;
  
  // Actions - Legacy
  setRoomCode: (code: string) => void;
  setPlayers: (players: Player[]) => void;
  addPlayer: (player: Player) => void;
  removePlayer: (userId: string) => void;
  updatePlayer: (userId: string, updates: Partial<Player>) => void;
  setCurrentQuestion: (question: Question, index: number) => void;
  setTimeRemaining: (time: number) => void;
  setGameStatus: (status: GameStatus) => void;
  submitAnswer: (answerId: string) => void;
  useLifeline: (type: string) => void;
  revealCorrectAnswer: (correctAnswerId: string) => void;
  reset: () => void;
  resetTeamsOnly: () => void;
}

const POINTS_OPTIONS = [200, 400, 600];

export const useGameStore = create<GameState>()(
  persist(
    (set, _get) => ({
      // Initial state
      gameSettings: null,
      team1: null,
      team2: null,
      currentTurn: 'team1',
      questionCells: [],
      currentCell: null,
      nextQuestionIsPit: false,
      shownQuestionIds: [],
      roomCode: null,
      players: [],
      currentQuestion: null,
      currentQuestionIndex: 0,
      totalQuestions: 0,
      timeRemaining: 0,
      gameStatus: 'Lobby',
      myAnswer: null,
      usedLifelines: [],

      // Game Settings Actions
      setGameSettings: (settings) => set({ gameSettings: settings }),

      // Team Actions
      setTeam1: (team) => set({ team1: team }),
      setTeam2: (team) => set({ team2: team }),
      
      switchTurn: () => set((state) => ({
        currentTurn: state.currentTurn === 'team1' ? 'team2' : 'team1',
      })),
      
      addScore: (teamId, points) => set((state) => {
        if (teamId === 'team1' && state.team1) {
          return { team1: { ...state.team1, score: state.team1.score + points } };
        }
        if (teamId === 'team2' && state.team2) {
          return { team2: { ...state.team2, score: state.team2.score + points } };
        }
        return {};
      }),
      
      // يسمح بالنقاط السالبة (للحفرة والخصم اليدوي)
      subtractScore: (teamId, points) => set((state) => {
        if (teamId === 'team1' && state.team1) {
          return { team1: { ...state.team1, score: state.team1.score - points } };
        }
        if (teamId === 'team2' && state.team2) {
          return { team2: { ...state.team2, score: state.team2.score - points } };
        }
        return {};
      }),
      
      useTeamLifeline: (teamId, lifelineId) => set((state) => {
        if (teamId === 'team1' && state.team1) {
          return { 
            team1: { 
              ...state.team1, 
              usedLifelines: [...state.team1.usedLifelines, lifelineId] 
            } 
          };
        }
        if (teamId === 'team2' && state.team2) {
          return { 
            team2: { 
              ...state.team2, 
              usedLifelines: [...state.team2.usedLifelines, lifelineId] 
            } 
          };
        }
        return {};
      }),

      // Game Board Actions
      initializeGameBoard: () => set((state) => {
        if (!state.gameSettings) return {};
        
        const cells: QuestionCell[] = [];
        state.gameSettings.categories.forEach((category) => {
          POINTS_OPTIONS.forEach((points) => {
            // Right side cell
            cells.push({
              categoryId: category.id,
              categoryName: category.name,
              points,
              side: 'right',
              isAnswered: false,
            });
            // Left side cell
            cells.push({
              categoryId: category.id,
              categoryName: category.name,
              points,
              side: 'left',
              isAnswered: false,
            });
          });
        });
        
        return { 
          questionCells: cells,
          totalQuestions: cells.length,
        };
      }),
      
      setCurrentCell: (cell) => set({ currentCell: cell }),
      setNextQuestionIsPit: (value) => set({ nextQuestionIsPit: value }),
      addShownQuestionId: (questionId) => set((state) => ({
        shownQuestionIds: state.shownQuestionIds.includes(questionId)
          ? state.shownQuestionIds
          : [...state.shownQuestionIds, questionId],
      })),
      
      markCellAnswered: (categoryId, points, side, teamId) => set((state) => ({
        questionCells: state.questionCells.map((cell) =>
          cell.categoryId === categoryId && cell.points === points && cell.side === side
            ? { ...cell, isAnswered: true, answeredBy: teamId }
            : cell
        ),
      })),

      // Legacy Actions
      setRoomCode: (code) => set({ roomCode: code }),
      setPlayers: (players) => set({ players }),
      addPlayer: (player) => set((state) => ({
        players: [...state.players, player],
      })),
      removePlayer: (userId) => set((state) => ({
        players: state.players.filter((p) => p.userId !== userId),
      })),
      updatePlayer: (userId, updates) => set((state) => ({
        players: state.players.map((p) =>
          p.userId === userId ? { ...p, ...updates } : p
        ),
      })),
      setCurrentQuestion: (question, index) => set({
        currentQuestion: question,
        currentQuestionIndex: index,
        myAnswer: null,
        timeRemaining: question.timeLimit,
      }),
      setTimeRemaining: (time) => set({ timeRemaining: time }),
      setGameStatus: (status) => set({ gameStatus: status }),
      submitAnswer: (answerId) => set({ myAnswer: answerId }),
      useLifeline: (type) => set((state) => ({
        usedLifelines: [...state.usedLifelines, type],
      })),
      revealCorrectAnswer: (correctAnswerId) => set((state) => ({
        currentQuestion: state.currentQuestion
          ? {
              ...state.currentQuestion,
              answers: state.currentQuestion.answers.map((a) => ({
                ...a,
                isCorrect: a.id === correctAnswerId,
              })),
            }
          : null,
      })),
      
      reset: () => set({
        gameSettings: null,
        team1: null,
        team2: null,
        currentTurn: 'team1',
        questionCells: [],
        currentCell: null,
        nextQuestionIsPit: false,
        shownQuestionIds: [],
        roomCode: null,
        players: [],
        currentQuestion: null,
        currentQuestionIndex: 0,
        totalQuestions: 0,
        timeRemaining: 0,
        gameStatus: 'Lobby',
        myAnswer: null,
        usedLifelines: [],
      }),
      
      // Reset only teams (keep game settings for playing again)
      resetTeamsOnly: () => set((state) => ({
        team1: state.team1 ? { ...state.team1, score: 0, usedLifelines: [] } : null,
        team2: state.team2 ? { ...state.team2, score: 0, usedLifelines: [] } : null,
        currentTurn: 'team1',
        questionCells: state.questionCells.map((cell) => ({
          ...cell,
          isAnswered: false,
          answeredBy: undefined,
        })),
        currentCell: null,
        nextQuestionIsPit: false,
        shownQuestionIds: [],
        currentQuestion: null,
        myAnswer: null,
      })),
    }),
    {
      name: 'game-storage',
      partialize: (state) => ({
        gameSettings: state.gameSettings,
        team1: state.team1,
        team2: state.team2,
      }),
    }
  )
);
